/**
 * Public deep-link page for a single media item at `/media/:slug`.
 *
 * Renders the full asset (video player, embedded iframe, PDF viewer,
 * or full-bleed image) plus title, description, and a Share button
 * that copies the canonical URL to the clipboard.
 *
 * This is the page you paste into emails, texts, and radio-spot
 * landing pages — and the one that bumps `view_count` / `share_count`.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import api from "@/lib/api";
import { webOrigin } from "@/lib/nativeBridge";
import { Button } from "@/components/ui/button";
import { ShareNetworkIcon as ShareNetwork, DownloadIcon as Download, ArrowLeftIcon as ArrowLeft, SpinnerIcon as Spinner } from "@phosphor-icons/react";

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
};

function youTubeEmbedFromAny(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/i);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vi = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vi) return `https://player.vimeo.com/video/${vi[1]}`;
  return null;
}

export default function MediaItem() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/media/item/${slug}`)
      .then((r) => { if (alive) setItem(r.data); })
      .catch((e) => { if (alive) setNotFound(e?.response?.status === 404); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  const share = async () => {
    const url = `${webOrigin()}${window.location.pathname}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: item?.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      }
    } catch {
      // user cancelled — no toast
    }
    api.post(`/media/item/${slug}/track/share`).catch(() => {});
  };

  const trackDownload = () => {
    api.post(`/media/item/${slug}/track/download`).catch(() => {});
  };

  if (loading) {
    return (
      <FullShell>
        <div className="flex items-center justify-center py-32 text-[#666]">
          <Spinner size={32} className="animate-spin" />
        </div>
      </FullShell>
    );
  }
  if (notFound || !item) {
    return (
      <FullShell>
        <div className="max-w-2xl mx-auto py-24 text-center px-6">
          <h1 className="font-spectral text-3xl">We couldn't find that.</h1>
          <p className="text-[#666] mt-3">
            The link may have been retired or you may have a typo. Browse the full library below.
          </p>
          <Link
            to="/media"
            className="inline-flex items-center gap-2 mt-6 rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white px-5 py-2 text-sm font-semibold tracking-wide transition-colors"
            data-testid="media-item-back-to-library"
          >
            <ArrowLeft size={16} /> Back to Media Library
          </Link>
        </div>
      </FullShell>
    );
  }

  const cover = resolveUrl(item.cover_url);
  const media = resolveUrl(item.media_url);
  const embed = youTubeEmbedFromAny(item.embed_url) || item.embed_url;

  return (
    <FullShell>
      <Helmet>
        <title>{`${item.title} — Armstrong K9 Registry`}</title>
        <meta name="description" content={item.subtitle || item.description?.slice(0, 200) || ""} />
        <meta property="og:title" content={item.title} />
        <meta property="og:description" content={item.subtitle || ""} />
        {cover && <meta property="og:image" content={cover} />}
        <meta property="og:type" content={item.kind?.startsWith("video") ? "video.other" : "website"} />
      </Helmet>

      <section className="max-w-5xl mx-auto px-6 md:px-10 pt-10 pb-16">
        <Link
          to="/media"
          className="inline-flex items-center gap-1.5 text-sm text-[#666] hover:text-ink mb-6"
          data-testid="media-item-back"
        >
          <ArrowLeft size={14} /> Media & Press
        </Link>

        <p className="text-xs uppercase tracking-[0.2em] text-gold font-bold">
          {categoryLabel(item.category)}
        </p>
        <h1 className="font-spectral text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-2">
          {item.title}
        </h1>
        {item.subtitle && (
          <p className="text-base text-[#3F3F3F] mt-3 max-w-3xl leading-relaxed">
            {item.subtitle}
          </p>
        )}

        {/* Asset */}
        <div className="mt-8 rounded-2xl overflow-hidden border border-[#E5E5E5] bg-black" data-testid="media-item-asset">
          {item.kind === "video_file" && media && (
            <video
              src={media}
              poster={cover || undefined}
              controls
              playsInline
              className="w-full block bg-black aspect-video"
              data-testid="media-item-video-file"
            />
          )}
          {item.kind === "video_embed" && embed && (
            <div className="aspect-video bg-black">
              <iframe
                src={embed}
                title={item.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
                data-testid="media-item-video-embed"
              />
            </div>
          )}
          {item.kind === "pdf" && media && (
            <iframe
              src={media}
              title={item.title}
              className="w-full bg-white"
              style={{ height: "75vh" }}
              data-testid="media-item-pdf"
            />
          )}
          {item.kind === "image" && media && (
            <img
              src={media}
              alt={item.title}
              className="w-full block"
              data-testid="media-item-image"
            />
          )}
        </div>

        {/* Action bar */}
        <div className="flex flex-wrap items-center gap-3 mt-6">
          <Button onClick={share} data-testid="media-item-share">
            <ShareNetwork size={16} className="mr-2" /> Share link
          </Button>
          {(item.kind === "pdf" || item.kind === "image" || item.kind === "video_file") && media && (
            <a
              href={media}
              download
              onClick={trackDownload}
              data-testid="media-item-download"
              className="inline-flex items-center gap-2 rounded-full bg-white border border-[#E5E5E5] hover:bg-[#F4F4F4] text-ink px-5 py-2 text-sm font-semibold tracking-wide transition-colors"
            >
              <Download size={16} /> Download
            </a>
          )}
          {item.cta_url && item.cta_label && (
            <a
              href={item.cta_url}
              data-testid="media-item-cta"
              className="inline-flex items-center gap-2 rounded-full bg-gold hover:bg-[#b48f1c] text-ink px-5 py-2 text-sm font-semibold tracking-wide transition-colors"
            >
              {item.cta_label}
            </a>
          )}
        </div>

        {item.description && (
          <article className="prose prose-neutral max-w-3xl mt-10 whitespace-pre-line text-[#3F3F3F] leading-relaxed">
            {item.description}
          </article>
        )}
      </section>
    </FullShell>
  );
}

function categoryLabel(cat) {
  return {
    promo_video: "Promo Video",
    flyer: "Flyer",
    testimonial: "Testimonial",
    how_to: "How-To",
    press: "Press",
    other: "Media",
  }[cat] || "Media";
}

function FullShell({ children }) {
  return (
    <div className="min-h-screen bg-[#FAFAFA] font-manrope text-ink">
      <header className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="media-item-brand-link">
            <img src="/logo-256.png" alt="" className="h-8 w-8 rounded-full object-contain" />
            <span className="font-spectral text-base tracking-tight">Armstrong K9 Registry</span>
          </Link>
          <nav className="hidden sm:flex items-center gap-5 text-sm text-[#3F3F3F]">
            <Link to="/media" className="hover:text-ink" data-testid="media-item-nav-library">Media</Link>
            <Link to="/about" className="hover:text-ink" data-testid="media-item-nav-about">About</Link>
            <Link
              to="/register"
              className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white px-4 py-1.5 text-xs font-semibold tracking-wide transition-colors"
              data-testid="media-item-nav-register"
            >
              Join free
            </Link>
          </nav>
        </div>
      </header>
      {children}
      <footer className="bg-white border-t border-[#E5E5E5] py-8 mt-8">
        <div className="max-w-6xl mx-auto px-6 md:px-10 text-xs text-[#9b9b9b] flex flex-wrap items-center justify-between gap-3">
          <span>© Armstrong K9 Registry</span>
          <div className="flex items-center gap-4">
            <Link to="/media" className="hover:text-ink">Media</Link>
            <Link to="/about" className="hover:text-ink">About</Link>
            <Link to="/legal/terms" className="hover:text-ink">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-ink">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
